import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiException } from '../../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';

import { Spinner } from '../../components/ui/spinner';
import { Button } from '../../components/ui/button';
import { fmtCompetencia, fmtDateTime } from '../../lib/format';
import { Upload, FileText, AlertCircle, CheckCircle2 } from 'lucide-react';

interface ImportacaoItem {
  id: string;
  competencia: string;
  versao: number;
  status: string;
  totalLinhas: number;
  totalValidas: number;
  totalInvalidas: number;
  nomeOriginal: string;
  createdAt: string;
  processedAt: string | null;
}

interface ImportResult {
  arquivoId: string;
  competencia: string;
  versao: number;
  totalLinhas: number;
  totaisPorStatus: Array<{ status: string; descricao: string; qtd: number; totalReais: number }>;
}

export function ImportacaoListaPage() {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [lastResult, setLastResult] = useState<ImportResult | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['importacoes'],
    queryFn: () => api<{ items: ImportacaoItem[]; total: number }>('/importacao/'),
  });

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('arquivo', file);
      return api<ImportResult>('/importacao/etipi', { method: 'POST', formData: fd });
    },
    onSuccess: (result) => {
      setLastResult(result);
      setLastError(null);
      qc.invalidateQueries({ queryKey: ['importacoes'] });
    },
    onError: (err: ApiException) => {
      setLastError(err?.apiError?.message ?? 'Falha ao importar arquivo');
      setLastResult(null);
    },
  });

  const [procResult, setProcResult] = useState<any | null>(null);
  const [procError, setProcError] = useState<string | null>(null);
  const processar = useMutation({
    mutationFn: (competencia: string) =>
      api<any>('/importacao/processar-mes-completo', { method: 'POST', body: { competencia } }),
    onSuccess: (r) => { setProcResult(r); setProcError(null); qc.invalidateQueries({ queryKey: ['importacoes'] }); },
    onError: (err: ApiException) => { setProcError(err?.apiError?.message ?? 'Falha ao processar'); setProcResult(null); },
  });

  function handleFile(file: File | null | undefined) {
    if (!file) return;
    upload.mutate(file);
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight">Importação ETIPI</h1>
        <p className="text-muted-foreground">Arquivos de retorno da consignatária iNETConsig</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Novo arquivo</CardTitle>
          <CardDescription>TXT posicional, encoding ISO-8859-1. Até 100 MB.</CardDescription>
        </CardHeader>
        <CardContent>
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              handleFile(e.dataTransfer.files?.[0]);
            }}
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
              dragOver ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50'
            }`}
            onClick={() => fileRef.current?.click()}
          >
            <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
            <p className="text-sm font-medium">
              {upload.isPending ? 'Processando arquivo…' : 'Arraste o arquivo aqui ou clique para selecionar'}
            </p>
            <p className="text-xs text-muted-foreground mt-1">Apenas arquivos .txt</p>
            <input
              ref={fileRef}
              type="file"
              accept=".txt,text/plain"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
          </div>

          {upload.isPending && (
            <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
              <Spinner />Processando, aguarde…
            </div>
          )}

          {lastError && (
            <div className="mt-4 flex items-start gap-2 p-3 border border-destructive/30 bg-destructive/10 rounded-md text-sm">
              <AlertCircle className="h-4 w-4 text-destructive mt-0.5" />
              <div>
                <p className="font-medium text-destructive">Falha</p>
                <p className="text-destructive/90">{lastError}</p>
              </div>
            </div>
          )}

          {lastResult && (
            <div className="mt-4 p-4 border border-green-300 bg-green-50 rounded-md">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="h-4 w-4 text-green-700" />
                <span className="font-medium text-green-900">Importado com sucesso</span>
              </div>
              <div className="text-sm text-green-900 space-y-1">
                <p><strong>Competência:</strong> {fmtCompetencia(lastResult.competencia)} · versão {lastResult.versao}</p>
                <p><strong>Total de linhas:</strong> {lastResult.totalLinhas.toLocaleString('pt-BR')}</p>
                <ul className="ml-4 mt-2 space-y-0.5">
                  {lastResult.totaisPorStatus.map((t) => (
                    <li key={t.status}>
                      Status {t.status} ({t.descricao}): <strong>{t.qtd.toLocaleString('pt-BR')}</strong> · R$ {t.totalReais.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </li>
                  ))}
                </ul>
                <Link to={`/importacao/${lastResult.arquivoId}`} className="inline-block mt-2 text-primary hover:underline">
                  Ver detalhes →
                </Link>
              </div>

              <div className="mt-4 pt-3 border-t border-green-300">
                <p className="text-sm text-green-900 mb-2">
                  Agora processe a competência para gerar associados novos, parcelas, ciclos e renovações:
                </p>
                <Button
                  disabled={processar.isPending}
                  onClick={() => { setProcResult(null); setProcError(null); processar.mutate(lastResult.competencia); }}
                >
                  {processar.isPending ? 'Processando…' : `Processar ${fmtCompetencia(lastResult.competencia)}`}
                </Button>
              </div>
            </div>
          )}

          {procError && (
            <div className="mt-3 p-3 border border-red-300 bg-red-50 dark:bg-red-950 dark:border-red-800 rounded-md text-sm text-red-800 dark:text-red-300">
              {procError}
            </div>
          )}
          {procResult && (
            <div className="mt-3 p-4 border border-blue-300 bg-blue-50 dark:bg-blue-950/40 dark:border-blue-800 rounded-md">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="h-4 w-4 text-blue-700 dark:text-blue-400" />
                <span className="font-medium text-blue-900 dark:text-blue-300">Competência {fmtCompetencia(procResult.competencia)} processada</span>
              </div>
              <ul className="text-sm text-blue-900 dark:text-blue-300 space-y-0.5 ml-1">
                <li>Associados novos: <strong>{procResult.associadosNovos}</strong></li>
                <li>Parcelas adicionadas: <strong>{procResult.parcelasAdicionadas}</strong></li>
                <li>Contratos novos: <strong>{procResult.contratosNovos}</strong></li>
                <li>Ciclos no sistema: <strong>{procResult.ciclosCriados}</strong> (aptos a renovar: <strong>{procResult.ciclosAptos}</strong>)</li>
                <li>Novos cards de renovação: <strong>{procResult.cardsRenovacaoCriados}</strong></li>
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Histórico</CardTitle>
          <CardDescription>{data?.total ?? 0} importações</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading && <Spinner />}
          {!isLoading && data && data.items.length === 0 && (
            <p className="text-sm text-muted-foreground">Nenhuma importação ainda.</p>
          )}
          {!isLoading && data && data.items.length > 0 && (
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground border-b">
                <tr>
                  <th className="text-left py-2">Arquivo</th>
                  <th className="text-left py-2">Competência</th>
                  <th className="text-left py-2">Versão</th>
                  <th className="text-right py-2">Linhas</th>
                  <th className="text-right py-2">Aplicadas</th>
                  <th className="text-right py-2">Rejeitadas</th>
                  <th className="text-left py-2 pl-4">Processado em</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((i) => (
                  <tr key={i.id} className="border-b last:border-0 hover:bg-muted/30">
                    <td className="py-2">
                      <Link to={`/importacao/${i.id}`} className="text-primary hover:underline flex items-center gap-2">
                        <FileText className="h-3 w-3" />{i.nomeOriginal}
                      </Link>
                    </td>
                    <td className="py-2">{fmtCompetencia(i.competencia)}</td>
                    <td className="py-2">v{i.versao}</td>
                    <td className="py-2 text-right">{i.totalLinhas.toLocaleString('pt-BR')}</td>
                    <td className="py-2 text-right text-green-600">{i.totalValidas.toLocaleString('pt-BR')}</td>
                    <td className="py-2 text-right text-amber-600">{i.totalInvalidas.toLocaleString('pt-BR')}</td>
                    <td className="py-2 pl-4 text-xs text-muted-foreground">{fmtDateTime(i.processedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
